import _ from 'lodash';
import { bigImageForDetail, imageUrlForList } from './products';

// Images are kept in the regular browser (HTTP) cache, so how long they stay
// cached depends on the Cache-Control header of the image server.

const CONCURRENCY = 6;
const REFRESH_PENDING_KEY = 'imageCacheRefreshPending';

// Sync reloads the page, so the "replace the image cache" request is kept in
// localStorage and only cleared once all images have been downloaded again
export const requestImageCacheRefresh = () => {
  try {
    localStorage.setItem(REFRESH_PENDING_KEY, 'true');
  } catch (e) {
    // storage unavailable - images are just not replaced
  }
};

export const isImageCacheRefreshPending = () => {
  try {
    return localStorage.getItem(REFRESH_PENDING_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

export const clearImageCacheRefresh = () => {
  try {
    localStorage.removeItem(REFRESH_PENDING_KEY);
  } catch (e) {
    // ignore
  }
};

// Both sizes the app shows: product lists and the product detail page
export const getImageUrlsToCache = (imageIds) => _.flatMap(
  _.uniq(_.compact(imageIds)),
  (id) => [imageUrlForList(id), bigImageForDetail(id)],
);

// Downloads the images into the browser cache. With refresh the cached copies
// are bypassed and replaced. Resolves with the number of network failures.
export const cacheImages = async (urls, {
  refresh = false,
  onProgress = () => {},
  isCancelled = () => false,
}) => {
  let next = 0;
  let done = 0;
  let failed = 0;

  const worker = async () => {
    while (next < urls.length && !isCancelled()) {
      const url = urls[next];
      next += 1;
      try {
        // no-cors like an <img>, so the cached response is the one <img> uses.
        // Missing images (404) still resolve and are simply skipped.
        // eslint-disable-next-line no-await-in-loop
        await fetch(url, {
          mode: 'no-cors',
          credentials: 'include',
          cache: refresh ? 'reload' : 'default',
        });
      } catch (e) {
        failed += 1;
      }
      done += 1;
      onProgress(done, urls.length);
    }
  };

  await Promise.all(_.times(CONCURRENCY, worker));
  return { failed, cancelled: isCancelled() };
};
